import React from 'react';

import { Route } from 'react-router-dom';

import { PERMISSION_KEYS } from 'constants-permissions';
import type { UserType } from 'application-types';
import { useInspectionsTemplateLibraryEnabled } from 'react-hooks';

import { hasPermissionOrSuperUser } from 'lane-shared/helpers/hasPermission';
import { InspectionsDashboard } from '../pages/InspectionsDashboard';
import { InspectionsTemplateLibrary } from '../pages/InspectionsTemplateLibrary';
import { useInspectionsAdminDashboardFlag } from '../hooks/useInspectionsAdminDashboardFlag';
import type { InspectionsChannel } from '../types/channel';
import {
  DASHBOARD_PATH,
  TEMPLATE_LIBRARY_EDIT_PATH,
  TEMPLATE_LIBRARY_PATH,
} from './useInspectionsNavigation';
import { InspectionTemplateCustomizePage } from '../pages/InspectionTemplateCustomizePage';

export function InspectionsRoutes({
  routePrefix,
  urlPrefix,
  channel,
  user,
}: {
  routePrefix: string;
  urlPrefix: string;
  channel?: InspectionsChannel | undefined;
  user: UserType;
}): JSX.Element | null {
  const inspectionsAdminDashboardFlag = useInspectionsAdminDashboardFlag();
  const inspectionsTemplateLibraryFlag = useInspectionsTemplateLibraryEnabled();

  if (!channel?.settings?.hasInspectionsEnabled) {
    return null;
  }

  const canAccessDashboard =
    inspectionsAdminDashboardFlag &&
    hasPermissionOrSuperUser(
      user,
      [PERMISSION_KEYS.PERMISSION_INSPECTIONS_COMPLETE],
      channel._id
    );

  const canAccessTemplateLibrary =
    inspectionsTemplateLibraryFlag &&
    hasPermissionOrSuperUser(
      user,
      [PERMISSION_KEYS.PERMISSION_INSPECTIONS_TEMPLATES_MANAGE],
      channel._id
    );

  if (!canAccessDashboard && !canAccessTemplateLibrary) {
    return null;
  }

  return (
    <>
      {canAccessDashboard && (
        <Route exact path={`${routePrefix}/${DASHBOARD_PATH}`}>
          <InspectionsDashboard />
        </Route>
      )}
      {canAccessTemplateLibrary && (
        <>
          <Route exact path={`${routePrefix}/${TEMPLATE_LIBRARY_PATH}`}>
            <InspectionsTemplateLibrary
              channel={channel}
              channelBasePath={urlPrefix}
            />
          </Route>
          <Route exact path={`${routePrefix}/${TEMPLATE_LIBRARY_EDIT_PATH}`}>
            <InspectionTemplateCustomizePage channelBasePath={urlPrefix} />
          </Route>
        </>
      )}
    </>
  );
}
